/**
 * 1000+ generated dummy records per category for hotels, cars, packages, cruises.
 */

import { seededRandomPrice } from '../utils/pricing';

const CITIES = [
  'Paris', 'London', 'Tokyo', 'Dubai', 'New York', 'Miami', 'Bangkok', 'Amsterdam',
  'Chicago', 'Los Angeles', 'Toronto', 'Barcelona', 'Rome', 'Singapore', 'Sydney',
  'Berlin', 'Prague', 'Vienna', 'Lisbon', 'Dublin', 'Seoul', 'Hong Kong', 'Cairo',
  'Istanbul', 'Athens', 'Mexico City', 'Rio de Janeiro', 'Cape Town', 'Zurich',
  'Stockholm', 'Oslo', 'Copenhagen', 'Helsinki', 'Brussels', 'Warsaw', 'Budapest',
  'Orlando', 'Las Vegas', 'San Francisco', 'Boston', 'Denver', 'Seattle', 'Atlanta',
  'Dallas', 'Phoenix', 'Montreal', 'Vancouver', 'Auckland', 'Melbourne', 'Delhi',
];

const HOTEL_BRANDS = [
  'Grand Vista', 'Harbor Inn', 'City Lights', 'Sunset Suites', 'Metro Stay',
  'Royal Palm', 'Skyline Tower', 'Emerald Resort', 'Pacific Lodge', 'Alpine Retreat',
  'Urban Loft', 'Seaside Haven', 'Golden Gate', 'Imperial Court', 'Garden Plaza',
];

/** Generic supplier types — real rental brands are never shown next to sample prices. */
const CAR_COMPANIES = ['Airport rental desk', 'City rental desk', 'National rental brand', 'Local rental agency'];
const CAR_TYPES = ['Economy', 'Compact', 'SUV', 'Luxury', 'Van', 'Midsize', 'Convertible'];
const CRUISE_LINES = ['Ocean Pearl', 'Star Horizon', 'Royal Wave', 'Blue Meridian', 'Coral Queen', 'Atlantic Crown'];
const CABINS = ['Interior', 'Ocean View', 'Balcony', 'Suite'];
const AMENITIES_POOL = ['Free WiFi', 'Breakfast', 'Pool', 'Gym', 'Spa', 'Parking', 'Airport Shuttle', 'Pet Friendly'];

function slug(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

function pick(arr, i) {
  return arr[i % arr.length];
}

function generateHotels(count = 1200) {
  return Array.from({ length: count }, (_, i) => {
    const city = pick(CITIES, i + Math.floor(i / CITIES.length));
    const stars = 3 + (i % 3);
    const rating = (3.5 + (i % 15) / 10).toFixed(1);
    const rooms = 1 + (i % 3);
    const perNight = seededRandomPrice(`hotel-${i}`);
    const price = perNight * rooms;

    return {
      id: `ht-catalog-${i}`,
      name: `${pick(HOTEL_BRANDS, i)} ${city}`,
      destination: city,
      citySlug: slug(city),
      stars,
      rating,
      amenities: AMENITIES_POOL.slice(0, 3 + (i % 4)),
      price,
      perNight,
      guests: 2 + (i % 3),
      rooms,
      address: `${100 + (i % 900)} ${pick(['Main St', 'Ocean Ave', 'Park Blvd', 'Market St'], i)}, ${city}`,
      source: 'catalog',
    };
  });
}

function generateCars(count = 1200) {
  const locations = CITIES.flatMap((c) => [`${c} Airport`, `${c} Downtown`, `${c} Station`]);

  return Array.from({ length: count }, (_, i) => {
    const type = pick(CAR_TYPES, i);
    const days = 1 + (i % 10);
    const perDay = seededRandomPrice(`car-${i}`);
    const price = perDay * days;

    return {
      id: `cr-catalog-${i}`,
      type,
      company: pick(CAR_COMPANIES, i),
      pickup: pick(locations, i),
      pickupSlug: slug(pick(locations, i)),
      seats: type === 'Van' ? 7 : type === 'SUV' ? 5 : 4,
      transmission: i % 5 === 0 ? 'Manual' : 'Automatic',
      price,
      perDay,
      days,
      source: 'catalog',
    };
  });
}

function generatePackages(count = 1000) {
  return Array.from({ length: count }, (_, i) => {
    const destination = pick(CITIES, i * 2);
    const nights = 3 + (i % 8);
    const travelers = 2;
    const perPerson = seededRandomPrice(`package-${i}`);
    const price = perPerson * travelers;

    return {
      id: `pk-catalog-${i}`,
      title: `${destination} Escape ${nights}N/${nights + 1}D`,
      destination,
      citySlug: slug(destination),
      includes: ['Flights', 'Hotel', 'Transfers', 'Tours'].slice(0, 2 + (i % 3)),
      nights,
      price,
      perPerson,
      travelers,
      source: 'catalog',
    };
  });
}

function generateCruises(count = 1000) {
  const regions = ['Caribbean', 'Mediterranean', 'Alaska', 'Northern Europe', 'Asia Pacific', 'South America'];
  const ports = ['Miami', 'Fort Lauderdale', 'Barcelona', 'Rome', 'Seattle', 'Southampton', 'Sydney', 'Singapore'];

  return Array.from({ length: count }, (_, i) => {
    const nights = 5 + (i % 12);
    const price = seededRandomPrice(`cruise-${i}`);

    return {
      id: `cu-catalog-${i}`,
      line: pick(CRUISE_LINES, i),
      port: pick(ports, i),
      destination: pick(regions, i),
      portSlug: slug(pick(ports, i)),
      regionSlug: slug(pick(regions, i)),
      nights,
      departDate: `2026-${String(6 + (i % 6)).padStart(2, '0')}-${String(5 + (i % 24)).padStart(2, '0')}`,
      price,
      cabin: pick(CABINS, i),
      source: 'catalog',
    };
  });
}

const catalog = {
  hotels: generateHotels(1200),
  cars: generateCars(1200),
  packages: generatePackages(1000),
  cruises: generateCruises(1000),
};

export const CATALOG_STATS = {
  hotels: catalog.hotels.length,
  cars: catalog.cars.length,
  packages: catalog.packages.length,
  cruises: catalog.cruises.length,
  total:
    catalog.hotels.length +
    catalog.cars.length +
    catalog.packages.length +
    catalog.cruises.length,
};

export function getHotelCatalog() {
  return catalog.hotels;
}

export function getCarCatalog() {
  return catalog.cars;
}

export function getPackageCatalog() {
  return catalog.packages;
}

export function getCruiseCatalog() {
  return catalog.cruises;
}
